import { useQuery } from '@apollo/client';
import { ApplicationAnswer, Document, Field, FieldType } from '@bybeam/platform-types';
import { CloseOutlined, ExpandOutlined } from '@mui/icons-material';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import {
  Box,
  Button,
  DataList,
  Dialog,
  Flex,
  Heading,
  HoverCard,
  IconButton,
  Inset,
  Table,
  Text,
} from '@radix-ui/themes';
import { Link } from 'react-router-dom';
import styles from './ApplicationFieldReviews.module.css';
import GetApplicationFieldReviews from './GetApplicationFieldReviewsQuery.graphql';

interface ApplicationSubmission {
  mappedAnswers: ReviewSectionProps[];
}

interface FieldResponse {
  field: Field;
  answer: ApplicationAnswer;
}
interface QuestionResponse {
  key: string;
  name: string;
  copy: string;
  fields: FieldResponse[];
}

interface QuestionGroupResponse {
  key: string;
  name: string;
  questions: QuestionResponse[];
}
interface ReviewSectionProps {
  key: string;
  name: string;
  questionGroups: QuestionGroupResponse[];
}

const formatKey = (key: string) => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/\.|(^.)/g, (str, offset) => {
      if (str === '.') {
        return ' > ';
      }
      return offset === 0 ? str.toUpperCase() : str;
    })
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

enum DocumentType {
  Image = 'image',
  PDF = 'pdf',
  CSV = 'csv',
  Other = 'other',
}

const cropFilename = (filename: string) => {
  return filename.length > 10 ? `${filename.slice(0, 3)}[...]${filename.slice(-4)}` : filename;
};

const getDocumentType = (mimetype: string): DocumentType => {
  if (mimetype.match('image.*')) return DocumentType.Image;
  if (mimetype?.match('pdf')) return DocumentType.PDF;
  if (mimetype?.match('csv')) return DocumentType.CSV;
  return DocumentType.Other;
};

const filterSectionsForResubmission = (
  latestSubmission: ApplicationSubmission,
): ReviewSectionProps[] => {
  if (!latestSubmission?.mappedAnswers) {
    return [];
  }

  const resubmissionSections: ReviewSectionProps[] = latestSubmission.mappedAnswers
    .map((sectionResponse: ReviewSectionProps) => {
      const filteredQuestionGroups = sectionResponse.questionGroups
        .map((questionGroupResponse: QuestionGroupResponse) => ({
          ...questionGroupResponse,
          questions: questionGroupResponse.questions
            .map((questionResponse) => ({
              ...questionResponse,
              fields: questionResponse.fields.filter(
                (fieldResponse) =>
                  fieldResponse.answer?.fieldReviewsRequiringResubmission?.length > 0,
              ),
            }))
            .filter((question) => question.fields.length > 0),
        }))
        .filter((group) => group.questions.length > 0);

      if (filteredQuestionGroups.length === 0) {
        return null;
      }

      return {
        ...sectionResponse,
        questionGroups: filteredQuestionGroups,
      };
    })
    .filter((section) => section !== null) as ReviewSectionProps[];

  return resubmissionSections;
};

// TODO: Resolve documents in API so we don't have to stitch together like this
const FieldResponseValue = ({
  fieldResponse,
  documents,
}: { fieldResponse: FieldResponse; documents: Document[] }) => {
  const document = documents.find(({ id }) => id === fieldResponse?.answer?.value);

  if (!fieldResponse?.answer?.value) {
    return <Text color="gold">Not answered</Text>;
  }

  switch (fieldResponse?.field.type) {
    case FieldType.Document:
      return document ? (
        <DocumentPreview document={document} fieldResponse={fieldResponse} />
      ) : (
        <></>
      );
    case FieldType.Complex:
      return <ComplexResponsePreview fieldResponse={fieldResponse} />;
    default:
      return JSON.stringify(fieldResponse.answer?.value, null, 2);
  }
};

const DocumentPreview = ({
  document,
  fieldResponse,
}: { document: Document; fieldResponse: FieldResponse }) => {
  return (
    <Dialog.Root>
      <HoverCard.Root>
        <HoverCard.Trigger>
          <Dialog.Trigger>
            <Flex gap="1" align="center" asChild>
              <button type="button">
                <Box height="2rem" width="2rem">
                  {getDocumentType(document.mimetype) === DocumentType.Image ? (
                    <img
                      title={document.filename}
                      src={document.previewUrl}
                      className={styles.documentPreview}
                      alt={`Preview of document upload for ${fieldResponse.field.key}`}
                    />
                  ) : getDocumentType(document.mimetype) === DocumentType.PDF ? (
                    <object
                      data={`${document.previewUrl}`}
                      className={styles.documentPreview}
                      aria-label={`Preview document upload for ${fieldResponse.field.key}`}
                    />
                  ) : null}
                </Box>
                {cropFilename(document.filename)}
              </button>
            </Flex>
          </Dialog.Trigger>
        </HoverCard.Trigger>
        <HoverCard.Content minWidth="240px" maxWidth="360px" sideOffset={4} alignOffset={16}>
          <Inset>
            <Flex direction="column">
              <Flex p="2" justify="between" gap="2">
                <Text size="1">{document.filename}</Text>
                <Dialog.Trigger>
                  <IconButton
                    variant="ghost"
                    aria-label="View full screen"
                    color="gray"
                    className={styles.fullScreenButton}
                  >
                    <ExpandOutlined />
                  </IconButton>
                </Dialog.Trigger>
              </Flex>
              <Flex
                align="center"
                justify="center"
                minHeight="8rem"
                className={styles.documentHoverImageContainer}
              >
                {getDocumentType(document.mimetype) === DocumentType.Image ? (
                  <img
                    title={document.filename}
                    src={document.previewUrl}
                    className={styles.document}
                    alt={`Document upload for ${fieldResponse.field.key}`}
                  />
                ) : getDocumentType(document.mimetype) === DocumentType.PDF ? (
                  <iframe
                    src={document.previewUrl}
                    title={`Document upload for ${fieldResponse.field.key}`}
                    className={styles.documentHoverImage}
                  />
                ) : null}
              </Flex>
            </Flex>
          </Inset>
        </HoverCard.Content>
      </HoverCard.Root>
      <Dialog.Content size="1" maxWidth="90vw" height="90vh" maxHeight="90vh">
        <Flex direction="column" height="100%" width="100%" minHeight="0">
          <Flex width="100%" justify="end">
            <Dialog.Close>
              <IconButton variant="soft" size="2" className={styles.fullScreenCloseButton}>
                <CloseOutlined height="24" width="24" />
              </IconButton>
            </Dialog.Close>
          </Flex>
          {getDocumentType(document.mimetype) === DocumentType.Image ? (
            <Flex
              width="100%"
              height="100%"
              justify="center"
              align="center"
              className={styles.fullScreenDocumentImage}
              minHeight="0"
            >
              <img
                title={document.filename}
                src={document.previewUrl}
                className={styles.fullScreenDocument}
                alt={`Document upload for ${fieldResponse.field.key}`}
              />
            </Flex>
          ) : getDocumentType(document.mimetype) === DocumentType.PDF ? (
            <iframe
              src={document.previewUrl}
              className={styles.fullScreenDocumentPDF}
              title={`Document upload for ${fieldResponse.field.key}`}
            />
          ) : null}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

const ComplexResponsePreview = ({ fieldResponse }: { fieldResponse: FieldResponse }) => {
  const response: Record<string, string> = JSON.parse(fieldResponse.answer?.value);

  return (
    <Dialog.Root>
      <HoverCard.Root>
        <HoverCard.Trigger>
          <Dialog.Trigger>
            <Flex asChild gap="1" align="center">
              <button type="button">{Object.entries(response).shift()?.[1]}</button>
            </Flex>
          </Dialog.Trigger>
        </HoverCard.Trigger>
        <HoverCard.Content sideOffset={4} alignOffset={16}>
          <DataList.Root>
            {Object.entries(response).map(([key, value]) => {
              return (
                <DataList.Item key={key}>
                  <DataList.Label>
                    {formatKey(key.replace(`${fieldResponse.field.key}_`, ''))}
                  </DataList.Label>
                  <DataList.Value>{value}</DataList.Value>
                </DataList.Item>
              );
            })}
          </DataList.Root>
        </HoverCard.Content>
      </HoverCard.Root>
      <Dialog.Content>
        <DataList.Root>
          {Object.entries(response).map(([key, value]) => {
            return (
              <DataList.Item key={key}>
                <DataList.Label>
                  {formatKey(key.replace(`${fieldResponse.field.key}_`, ''))}
                </DataList.Label>
                <DataList.Value>{value}</DataList.Value>
              </DataList.Item>
            );
          })}
        </DataList.Root>
      </Dialog.Content>
    </Dialog.Root>
  );
};

function ReviewSection({
  reviewSection,
  documents,
  applicationId,
  diffVersion,
}: {
  reviewSection: ReviewSectionProps;
  documents: Document[];
  applicationId: string;
  diffVersion?: ReviewSectionProps;
}) {
  return (
    <Flex gap="4" direction="column">
      {reviewSection?.questionGroups?.map(
        (questionGroupResponse: QuestionGroupResponse, questionGroupIndex: number) => {
          return (
            <div key={questionGroupResponse.key}>
              <Table.Root size="1" variant="surface">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell width={'6rem'} maxWidth="6rem">
                      Question
                    </Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell width={'6rem'} maxWidth="6rem">
                      Previous Value
                    </Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell width={'6rem'} maxWidth="6rem">
                      New Value
                    </Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {questionGroupResponse.questions.map((questionResponse, questionIndex) => {
                    return questionResponse.fields.map((fieldResponse, fieldIndex) => {
                      const diffResponse = diffVersion?.questionGroups
                        .find(({ key }) => {
                          return questionGroupResponse.key === key;
                        })
                        ?.questions.find(({ key }) => {
                          return questionResponse.key === key;
                        })
                        ?.fields.find(({ answer }) => {
                          return fieldResponse.answer && fieldResponse.answer?.key === answer.key;
                        });

                      return (
                        <Table.Row
                          align={'center'}
                          key={fieldResponse.field.key}
                          className={styles.tableRow}
                        >
                          <Table.Cell maxWidth="6rem">
                            <Flex px="1" align="center">
                              <Button asChild variant="ghost" title={fieldResponse.field.key}>
                                <Link
                                  to={makeRoute(
                                    makeRoute(ApplicantRoutes.QUESTION, {
                                      questionKey: questionResponse.key,
                                    }),
                                    { applicationId: applicationId },
                                  )}
                                >
                                  <span className={styles.fieldKey}>
                                    {formatKey(fieldResponse.field.key)}
                                  </span>
                                </Link>
                              </Button>
                            </Flex>
                          </Table.Cell>
                          <Table.Cell maxWidth="6rem">
                            <FieldResponseValue
                              fieldResponse={fieldResponse}
                              documents={documents || []}
                            />
                          </Table.Cell>
                          <Table.Cell maxWidth="6rem">
                            {diffResponse?.answer?.value !== fieldResponse?.answer?.value ? (
                              <FieldResponseValue
                                fieldResponse={diffResponse}
                                documents={documents || []}
                              />
                            ) : (
                              'Not updated'
                            )}
                          </Table.Cell>
                        </Table.Row>
                      );
                    });
                  })}
                </Table.Body>
              </Table.Root>
            </div>
          );
        },
      )}
    </Flex>
  );
}

interface ApplicationFieldReviewProps {
  applicationId: string;
}

export default function ApplicationFieldReviews({ applicationId }: ApplicationFieldReviewProps) {
  const { data, loading, error } = useQuery(GetApplicationFieldReviews, {
    variables: {
      applicationSubmissionId: applicationId,
    },
    // TODO: Improve cache policy in main application flow
    fetchPolicy: 'no-cache',
  });

  if (loading) {
    return <>Loading</>;
  }

  if (error) {
    return <>Error</>;
  }

  if (!data.applicationSubmission) {
    return <>Application not found</>;
  }

  const applicationSubmission = data.applicationSubmission;
  const reviewSections = filterSectionsForResubmission(applicationSubmission.latestSubmission);

  return (
    <>
      <>
        <Flex
          direction="column"
          gap="2"
          py="4"
          px="6"
          width="100%"
          className={styles.applicationFieldReviewsContainer}
        >
          {reviewSections.length > 0 && (
            <Box>
              <Heading size="5" weight="regular" mb="1">
                Requested Resubmissions
              </Heading>
              <Text>
                Please review the following fields and make the necessary changes, then resubmit
                your application.
              </Text>
              {reviewSections.map((reviewSection: ReviewSectionProps, index: number) => {
                return reviewSection ? (
                  <ReviewSection
                    key={reviewSection.key}
                    reviewSection={reviewSection}
                    documents={applicationSubmission.documents}
                    applicationId={applicationId}
                    diffVersion={applicationSubmission.currentVersion.mappedAnswers[index]}
                  />
                ) : null;
              })}
            </Box>
          )}
          <Flex direction="column" gap="2">
            <Text>
              After you update your responses, review your application and resubmit. Resubmitting
              without making requested changes may result in delays in processing your application.
            </Text>
            <Box>
              <Button variant="outline" asChild>
                <Link to={makeRoute(ApplicantRoutes.APPLICATION_REVIEW, { applicationId })}>
                  Review application
                </Link>
              </Button>
            </Box>
          </Flex>
        </Flex>
      </>
    </>
  );
}
